<template>
  <div class="pa-0 ticket-preview-wrapper" data-testid="ticket-preview">
    <v-skeleton-loader
      v-if="loading"
      type="list-item, divider, list-item-avatar, list-item-three-line@4, list-item-avatar-two-line@4"
      class="h-100"
    ></v-skeleton-loader>

    <div v-else-if="ticket" class="d-flex flex-column h-100 ticket-preview-inner">
      <div class="px-4">
        <slot name="header" :ticket-details="ticketDetails" />

        <v-tabs
          :model-value="selectedTab"
          class="mt-3"
          color="orange-base"
          background-color="white"
          slider-color="orange-base"
          :height="48"
          show-arrows="never"
          @update:model-value="$emit('tab-click', $event as TicketTab)"
        >
          <v-tab v-for="item in tabs" :key="item" :value="item" class="mr-8" :height="42">
            {{ $t(`ticketDetails.tabs.${item}`, { n: totalComments }) }}
          </v-tab>
        </v-tabs>
      </div>

      <div class="ticket-tab-content px-4 pb-2" id="ticket-tab-content">
        <v-window :model-value="selectedTab">
          <v-window-item :value="TicketTab.OVERVIEW">
            <div class="mt-1">
              <ticket-auto-close-notice :ticket-details="ticketDetails" />
              <ticket-preview-description :ticket-details="ticketDetails" class="mb-5" />
              <users-say-section
                v-if="ticket.suspiciousVoteCount || ticket.safeVoteCount"
                class="mb-5"
                :suspicious-vote-count="ticket.suspiciousVoteCount"
                :safe-vote-count="ticket.safeVoteCount"
              ></users-say-section>
            </div>

            <slot name="related-events" :ticket-details="ticketDetails" />

            <div>
              <tickets-remediation-section :ticket-details="ticketDetails" class="mb-5" />
              <v-divider />
            </div>
            <div>
              <ticket-general-info
                :ticket-details="ticketDetails"
                :is-soc="isSoc"
                :context="context"
                class="mt-5"
              />
            </div>
          </v-window-item>

          <v-window-item :value="TicketTab.DETAILS">
            <div class="mt-4">
              <key-details :ticket="ticketDetails" :context="context"></key-details>
              <ticket-full-details :ticket-details="ticketDetails" :config="fullDetailsConfig" />
            </div>
          </v-window-item>

          <v-window-item :value="TicketTab.ACTIVITY_LOGS">
            <div>
              <entity-activity-logs
                v-if="ticketDetails"
                :activity-logs="ticketDetails.activityLogs"
                :activity-logs-total="ticketDetails.activityLogsTotal"
                :entity-id="ticketDetails.eventId"
                :loading="activityLogsLoading"
                :hide-all-activity-link="hideAllActivityLink"
                :show-header="false"
                :access-restricted="activityLogsAccessRestricted"
                show-load-more
                search-prefix="ticketId"
                class="mb-5"
                @undo-clicked="$emit('undo-activity-log', $event)"
                @load-more-items-clicked="$emit('load-more-activity-logs')"
              />
            </div>
          </v-window-item>

          <v-window-item :value="TicketTab.COMMENTS">
            <ticket-comments
              v-if="ticketDetails"
              :ticket="ticketDetails"
              :comments="comments"
              :comment-not-allowed="commentNotAllowed"
              :total-comments="totalComments"
              :loading="commentsLoading"
              @add-comment="$emit('add-comment', $event)"
              @load-more="$emit('load-more-comments')"
            >
              <template #comment-actions="{ comment }">
                <slot name="comment-actions" :comment="comment" />
              </template>
            </ticket-comments>
          </v-window-item>
        </v-window>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import EntityActivityLogs from "@/components/EntityActivityLogs.vue";
import TicketGeneralInfo from "@/components/tickets/ticket-preview-details/TicketGeneralInfo.vue";
import KeyDetails from "@/components/tickets/ticket-preview-details/KeyDetails.vue";
import TicketComments from "@/components/tickets/ticket-preview-details/TicketComments.vue";
import TicketPreviewDescription from "@/components/tickets/ticket-preview-details/TicketPreviewDescription.vue";
import TicketAutoCloseNotice from "@/components/tickets/ticket-preview-details/TicketAutoCloseNotice.vue";
import TicketsRemediationSection from "@/components/tickets/ticket-preview-details/TicketsRemediationSection.vue";
import UsersSaySection from "@/components/tickets/ticket-preview-details/UsersSaySection.vue";
import TicketFullDetails from "@/components/tickets/ticket-preview-details/full-details/TicketFullDetails.vue";
import type { TicketFullDetailsConfig } from "@/components/tickets/ticket-preview-details/full-details/helpers/ticket-full-details.config";
import { TicketTab } from "@/constants/tickets";
import type { AppContext } from "@/types";
import type { Ticket, TicketDetails } from "@/_store/tickets/tickets.module";
import type { Comment, CommentSettings } from "@/_store/ticket-comments.module";

/**
 * Base component for ticket preview that provides shared template structure.
 * This component is used by both TicketPreview and MspTicketPreview components.
 *
 * Features:
 * - Tabbed interface (Overview, Details, Activity Logs, Comments)
 * - Skeleton loading state
 * - Slot-based customization for header, related events, and comment actions
 * - Event emission for parent component interactions
 *
 * @component
 * @example
 * <ticket-preview-base
 *   :ticket="ticket"
 *   :loading="loading"
 *   :ticket-details="ticketDetails"
 *   :selected-tab="selectedTab"
 *   :tabs="tabs"
 *   :context="AppContext.SINGLE_WORKSPACE"
 *   @tab-click="onTabClick"
 * >
 *   <template #header="{ ticketDetails }">
 *     <ticket-preview-header :ticket-details="ticketDetails" />
 *   </template>
 * </ticket-preview-base>
 */
export default defineComponent({
  components: {
    UsersSaySection,
    TicketFullDetails,
    TicketsRemediationSection,
    TicketPreviewDescription,
    TicketAutoCloseNotice,
    TicketGeneralInfo,
    EntityActivityLogs,
    KeyDetails,
    TicketComments,
  },

  props: {
    /** The ticket object to display (can be null before loading) */
    ticket: {
      type: Object as PropType<Ticket | null>,
      default: null,
    },
    /** Whether ticket details are currently being loaded */
    loading: {
      type: Boolean,
      default: false,
    },
    /** Detailed ticket information including sections, activity logs, etc. */
    ticketDetails: {
      type: Object as PropType<TicketDetails>,
      required: true,
    },
    /** Currently selected tab (Overview, Details, Activity Logs, or Comments) */
    selectedTab: {
      type: String as PropType<TicketTab>,
      required: true,
    },
    /** Array of available tabs to display */
    tabs: {
      type: Array as PropType<TicketTab[]>,
      required: true,
    },
    /** Application context (SINGLE_WORKSPACE, MSP, etc.) */
    context: {
      type: String as PropType<AppContext>,
      required: true,
    },
    /** Whether this is a SOC (Security Operations Center) context */
    isSoc: {
      type: Boolean,
      default: false,
    },
    /** Optional configuration for full details display */
    fullDetailsConfig: {
      type: Object as PropType<TicketFullDetailsConfig>,
      default: undefined,
    },
    /** Array of comments for this ticket */
    comments: {
      type: Array as PropType<Comment[]>,
      required: true,
    },
    /** Whether comments are currently being loaded */
    commentsLoading: {
      type: Boolean,
      default: false,
    },
    /** Total number of comments (for pagination) */
    totalComments: {
      type: Number,
      required: true,
    },
    /** Whether adding comments is disabled due to permissions */
    commentNotAllowed: {
      type: Boolean,
      default: false,
    },
    /** Whether activity logs are currently being loaded */
    activityLogsLoading: {
      type: Boolean,
      default: false,
    },
    /** Whether activity logs access is restricted due to permissions */
    activityLogsAccessRestricted: {
      type: Boolean,
      default: false,
    },
    /** Whether to hide the "view all activity" link */
    hideAllActivityLink: {
      type: Boolean,
      default: false,
    },
  },

  emits: {
    "tab-click": (_tab: TicketTab) => true,
    "load-more-activity-logs": () => true,
    "add-comment": (_payload: CommentSettings & { eventId: string; workspaceId?: string }) => true,
    "load-more-comments": () => true,
    "undo-activity-log": (_id: string) => true,
  },

  setup() {
    return {
      TicketTab,
    };
  },
});
</script>

<style scoped lang="scss">
.ticket-preview-wrapper {
  height: 100%;
}

.ticket-preview-inner {
  height: 100%;
}

.ticket-tab-content {
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.event-type-icon {
  width: 40px;
  height: 40px;
  font-size: 40px;
}

:deep(*) {
  .text-primary {
    .icon-warning-xs:before {
      color: rgb(var(--v-theme-primary));
    }
  }
}
</style>
